import { networkError, QubBaseError, toError, type Fetch, type HeaderSource } from './http';

export interface PostgrestResponse<T> {
  data: T | null;
  error: QubBaseError | null;
  count: number | null;
  status: number;
  statusText: string;
}

type CountOption = 'exact' | 'planned' | 'estimated';
type Row = Record<string, unknown>;

const quoteValue = (v: unknown) => {
  const s = String(v);
  return /[,()".:\s]/.test(s) ? `"${s.replace(/"/g, '\\"')}"` : s;
};

/**
 * Chainable query builder compatible with supabase-js / postgrest-js. A builder is a thenable: `await` it to
 * run the request. Errors are returned in `{ error }`, never thrown (call `.throwOnError()` to throw).
 */
export class QueryBuilder<T = Row, R = T[]> implements PromiseLike<PostgrestResponse<R>> {
  private readonly params = new URLSearchParams();
  private readonly headers: Record<string, string> = {};
  private method: 'GET' | 'HEAD' | 'POST' | 'PATCH' | 'DELETE' = 'GET';
  private body: unknown = undefined;
  private shouldThrow = false;
  private signal: AbortSignal | undefined;

  constructor(
    private readonly fetcher: Fetch,
    private readonly url: string,
    private readonly baseHeaders: HeaderSource,
    private readonly schema: string | null,
  ) {}

  // ---------------------------------------------------------------- verbs

  /** Columns to return; after insert/update/upsert/delete it asks for the affected rows back. */
  select<S = T>(columns = '*', options: { count?: CountOption; head?: boolean } = {}): QueryBuilder<T, S[]> {
    if (this.method === 'GET' || this.method === 'HEAD') this.method = options.head ? 'HEAD' : 'GET';
    else this.addPrefer('return=representation');
    this.params.set('select', columns.replace(/\s+(?=(?:[^"]*"[^"]*")*[^"]*$)/g, ''));
    if (options.count) this.addPrefer(`count=${options.count}`);
    return this as unknown as QueryBuilder<T, S[]>;
  }

  insert(values: Partial<T> | Partial<T>[], options: { count?: CountOption; defaultToNull?: boolean } = {}): QueryBuilder<T, null> {
    this.method = 'POST';
    this.body = values;
    if (Array.isArray(values)) {
      const cols = [...new Set(values.flatMap((v) => Object.keys(v as object)))];
      if (cols.length) this.params.set('columns', cols.map((c) => `"${c}"`).join(','));
    }
    if (options.defaultToNull === false) this.addPrefer('missing=default');
    if (options.count) this.addPrefer(`count=${options.count}`);
    return this as unknown as QueryBuilder<T, null>;
  }

  upsert(values: Partial<T> | Partial<T>[], options: { onConflict?: string; ignoreDuplicates?: boolean; count?: CountOption } = {}): QueryBuilder<T, null> {
    this.insert(values, { count: options.count });
    this.addPrefer(`resolution=${options.ignoreDuplicates ? 'ignore' : 'merge'}-duplicates`);
    if (options.onConflict) this.params.set('on_conflict', options.onConflict);
    return this as unknown as QueryBuilder<T, null>;
  }

  update(values: Partial<T>, options: { count?: CountOption } = {}): QueryBuilder<T, null> {
    this.method = 'PATCH';
    this.body = values;
    if (options.count) this.addPrefer(`count=${options.count}`);
    return this as unknown as QueryBuilder<T, null>;
  }

  delete(options: { count?: CountOption } = {}): QueryBuilder<T, null> {
    this.method = 'DELETE';
    if (options.count) this.addPrefer(`count=${options.count}`);
    return this as unknown as QueryBuilder<T, null>;
  }

  // ---------------------------------------------------------------- filters

  private filter(column: string, op: string, value: unknown): this {
    this.params.append(column, `${op}.${value}`);
    return this;
  }

  eq(column: string, value: unknown) {
    return this.filter(column, 'eq', value);
  }
  neq(column: string, value: unknown) {
    return this.filter(column, 'neq', value);
  }
  gt(column: string, value: unknown) {
    return this.filter(column, 'gt', value);
  }
  gte(column: string, value: unknown) {
    return this.filter(column, 'gte', value);
  }
  lt(column: string, value: unknown) {
    return this.filter(column, 'lt', value);
  }
  lte(column: string, value: unknown) {
    return this.filter(column, 'lte', value);
  }
  like(column: string, pattern: string) {
    return this.filter(column, 'like', pattern.replace(/%/g, '*'));
  }
  ilike(column: string, pattern: string) {
    return this.filter(column, 'ilike', pattern.replace(/%/g, '*'));
  }
  likeAnyOf(column: string, patterns: string[]) {
    return this.filter(column, 'like(any)', `{${patterns.map((p) => quoteValue(p.replace(/%/g, '*'))).join(',')}}`);
  }
  ilikeAnyOf(column: string, patterns: string[]) {
    return this.filter(column, 'ilike(any)', `{${patterns.map((p) => quoteValue(p.replace(/%/g, '*'))).join(',')}}`);
  }
  is(column: string, value: null | boolean) {
    return this.filter(column, 'is', value);
  }
  in(column: string, values: readonly unknown[]) {
    return this.filter(column, 'in', `(${values.map(quoteValue).join(',')})`);
  }
  contains(column: string, value: string | readonly unknown[] | Record<string, unknown>) {
    return this.filter(column, 'cs', typeof value === 'string' ? value : Array.isArray(value) ? `{${value.map(quoteValue).join(',')}}` : JSON.stringify(value));
  }
  containedBy(column: string, value: string | readonly unknown[] | Record<string, unknown>) {
    return this.filter(column, 'cd', typeof value === 'string' ? value : Array.isArray(value) ? `{${value.map(quoteValue).join(',')}}` : JSON.stringify(value));
  }
  overlaps(column: string, value: string | readonly unknown[]) {
    return this.filter(column, 'ov', typeof value === 'string' ? value : `{${value.map(quoteValue).join(',')}}`);
  }
  rangeGt(column: string, range: string) {
    return this.filter(column, 'sr', range);
  }
  rangeLt(column: string, range: string) {
    return this.filter(column, 'sl', range);
  }
  rangeGte(column: string, range: string) {
    return this.filter(column, 'nxl', range);
  }
  rangeLte(column: string, range: string) {
    return this.filter(column, 'nxr', range);
  }
  rangeAdjacent(column: string, range: string) {
    return this.filter(column, 'adj', range);
  }
  textSearch(column: string, query: string, options: { config?: string; type?: 'plain' | 'phrase' | 'websearch' } = {}) {
    const op = options.type === 'plain' ? 'plfts' : options.type === 'phrase' ? 'phfts' : options.type === 'websearch' ? 'wfts' : 'fts';
    return this.filter(column, `${op}${options.config ? `(${options.config})` : ''}`, query);
  }
  match(query: Record<string, unknown>) {
    for (const [k, v] of Object.entries(query)) this.eq(k, v);
    return this;
  }
  not(column: string, operator: string, value: unknown) {
    return this.filter(column, `not.${operator}`, value);
  }
  or(filters: string, options: { referencedTable?: string } = {}) {
    this.params.append(options.referencedTable ? `${options.referencedTable}.or` : 'or', `(${filters})`);
    return this;
  }

  // ---------------------------------------------------------------- modifiers

  order(column: string, options: { ascending?: boolean; nullsFirst?: boolean; referencedTable?: string } = {}) {
    const key = options.referencedTable ? `${options.referencedTable}.order` : 'order';
    const term = `${column}.${options.ascending === false ? 'desc' : 'asc'}${options.nullsFirst === undefined ? '' : options.nullsFirst ? '.nullsfirst' : '.nullslast'}`;
    const existing = this.params.get(key);
    this.params.set(key, existing ? `${existing},${term}` : term);
    return this;
  }

  limit(count: number, options: { referencedTable?: string } = {}) {
    this.params.set(options.referencedTable ? `${options.referencedTable}.limit` : 'limit', String(count));
    return this;
  }

  range(from: number, to: number, options: { referencedTable?: string } = {}) {
    const prefix = options.referencedTable ? `${options.referencedTable}.` : '';
    this.params.set(`${prefix}offset`, String(from));
    this.params.set(`${prefix}limit`, String(to - from + 1));
    return this;
  }

  abortSignal(signal: AbortSignal) {
    this.signal = signal;
    return this;
  }

  /** Expect exactly one row; the result is an object (error if zero or several rows). */
  single(): QueryBuilder<T, T> {
    this.headers.Accept = 'application/vnd.pgrst.object+json';
    return this as unknown as QueryBuilder<T, T>;
  }

  /** Zero or one row; `data` is null when nothing matched. */
  maybeSingle(): QueryBuilder<T, T | null> {
    this.headers['x-maybe-single'] = '1';
    return this as unknown as QueryBuilder<T, T | null>;
  }

  csv(): QueryBuilder<T, string> {
    this.headers.Accept = 'text/csv';
    return this as unknown as QueryBuilder<T, string>;
  }

  throwOnError(): this {
    this.shouldThrow = true;
    return this;
  }

  private addPrefer(value: string) {
    const current = this.headers.Prefer;
    this.headers.Prefer = current ? `${current},${value}` : value;
  }

  // ---------------------------------------------------------------- execution

  async execute(): Promise<PostgrestResponse<R>> {
    const maybeSingle = this.headers['x-maybe-single'] === '1';
    const headers: Record<string, string> = { ...(await this.baseHeaders()), ...this.headers };
    delete headers['x-maybe-single'];
    if (this.schema) headers[this.method === 'GET' || this.method === 'HEAD' ? 'Accept-Profile' : 'Content-Profile'] = this.schema;
    if (this.body !== undefined) headers['Content-Type'] = 'application/json';
    const qs = this.params.toString();
    let response: PostgrestResponse<R>;
    try {
      const res = await this.fetcher(`${this.url}${qs ? `?${qs}` : ''}`, {
        method: this.method,
        headers,
        body: this.body !== undefined ? JSON.stringify(this.body) : undefined,
        signal: this.signal,
      });
      const range = res.headers.get('content-range');
      const total = range?.split('/')[1];
      const count = total && total !== '*' ? Number(total) : null;
      if (!res.ok) {
        response = { data: null, error: await toError(res), count: null, status: res.status, statusText: res.statusText };
      } else {
        const text = this.method === 'HEAD' ? '' : await res.text();
        let data: unknown = null;
        if (text) data = headers.Accept === 'text/csv' ? text : JSON.parse(text);
        if (maybeSingle && Array.isArray(data)) {
          if (data.length > 1) {
            response = { data: null, error: new QubBaseError('JSON object requested, multiple rows returned', 406, 'PGRST116'), count, status: 406, statusText: 'Not Acceptable' };
            if (this.shouldThrow) throw response.error;
            return response;
          }
          data = data[0] ?? null;
        }
        response = { data: data as R, error: null, count, status: res.status, statusText: res.statusText };
      }
    } catch (err) {
      if (err instanceof QubBaseError) throw err;
      response = { data: null, error: networkError(err), count: null, status: 0, statusText: '' };
    }
    if (this.shouldThrow && response.error) throw response.error;
    return response;
  }

  then<A = PostgrestResponse<R>, B = never>(
    onfulfilled?: ((value: PostgrestResponse<R>) => A | PromiseLike<A>) | null,
    onrejected?: ((reason: unknown) => B | PromiseLike<B>) | null,
  ): PromiseLike<A | B> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

/** `client.rpc(fn, args)` — calls a Postgres function; set-returning results accept filters and modifiers. */
export class RpcBuilder<R = unknown> extends QueryBuilder<Row, R> {
  constructor(fetcher: Fetch, url: string, headers: HeaderSource, schema: string | null, args: Record<string, unknown>, options: { head?: boolean; get?: boolean; count?: CountOption }) {
    super(fetcher, url, headers, schema);
    const self = this as unknown as { method: string; body: unknown; params: URLSearchParams; headers: Record<string, string> };
    if (options.get || options.head) {
      self.method = options.head ? 'HEAD' : 'GET';
      for (const [k, v] of Object.entries(args)) self.params.set(k, String(v));
    } else {
      self.method = 'POST';
      self.body = args;
    }
    if (options.count) self.headers.Prefer = `count=${options.count}`;
  }
}
